import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { getSorting } from 'utils/general';
import { IBaseClientAdmin, IClientAdmin } from 'types/income';
import { ContentLanguage, TableDataResponse } from 'types/general';
import { RoleType } from 'store/permissions';
import {
  ClientAdminsFilters,
  ClientAdminStatus,
} from './admin-client-admins.types';

interface GetAdmins {
  clientId: string;
  filters: Partial<ClientAdminsFilters>;
}

interface GetAdmin {
  clientId: string;
  adminId: string;
}

interface CreateAdmin {
  clientId: string;
  data: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
    roleType: RoleType;
    customRoleId?: string;
    roleScopeCompanyIds?: string[];
  };
}

interface UpdateAdmin {
  clientId: string;
  adminId: string;
  data: Partial<
    CreateAdmin['data'] & {
      status: ClientAdminStatus;
      language: Uppercase<ContentLanguage>;
    }
  >;
}

interface DeleteAdmin {
  adminId: string;
}

interface SendInvite {
  clientId: string;
  adminId: string;
}

export const loaders = {
  getAdmins: ({ clientId, filters }: GetAdmins) => {
    return api<TableDataResponse<IBaseClientAdmin>>({
      url: Endpoints.clientAdmin.clients.byId.admins.root.replace(
        ':clientId',
        clientId,
      ),
      version: 'v2',
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        search: filters.search || undefined,
        filter: {
          adminIds: filters.admins,
          status: filters.status,
        },
      },
    });
  },
  getAdmin: ({ clientId, adminId }: GetAdmin) => {
    return api<IClientAdmin>({
      url: Endpoints.clientAdmin.clients.byId.admins.byId
        .replace(':clientId', clientId)
        .replace(':adminId', adminId),
    });
  },
  createAdmin: ({ clientId, data }: CreateAdmin) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.clients.byId.admins.create.replace(
        ':clientId',
        clientId,
      ),
      data,
      method: 'post',
    });
  },
  updateAdmin: ({ clientId, adminId, data }: UpdateAdmin) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.clients.byId.admins.byId
        .replace(':clientId', clientId)
        .replace(':adminId', adminId),
      data: {
        ...data,
        phone: data.phone
          ? data.phone.charAt(0) === '+'
            ? data.phone
            : `+${data.phone}`
          : undefined,
        email: data.email ? data.email.toLowerCase() : undefined,
      },
      method: 'patch',
    });
  },
  deleteAdmin: ({ adminId }: DeleteAdmin) => {
    return api({
      url: Endpoints.clientAdmin.companies.admins.delete.melp.replace(
        ':adminId',
        adminId,
      ),
      method: 'delete',
      context: 'admin',
    });
  },
  sendInvite: ({ clientId, adminId }: SendInvite) => {
    return api({
      url: Endpoints.clientAdmin.clients.byId.admins.sendInvite
        .replace(':clientId', clientId)
        .replace(':adminId', adminId),
      method: 'post',
    });
  },
};
